import { Tooltip as TooltipPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from 'cn';

/**
 * A one-line label for an icon button.
 *
 * The design system specifies it exactly (`docs/design/components/Tooltip`):
 * an inverted chip — `foreground` ground with `background` text — 12px / 500,
 * `shadow-2`, `radius-md`, after 300ms on hover **or focus**.
 *
 * Two rules from that README worth keeping in view:
 *
 * - **Every icon button has one, with the same text as its `aria-label`.** Not
 *   a shorter version and not a different wording: a tooltip that says
 *   something the screen reader does not is two different products.
 * - **One line only.** The word gloss and the citation preview are Popovers,
 *   not tooltips, because they hold structured content and must be reachable
 *   on touch. A tooltip never carries anything a touch user needs, which is
 *   why an icon button also keeps its accessible name.
 */

export function TooltipProvider({
  delayDuration = 300,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />;
}

export function TooltipRoot(props: ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root {...props} />;
}

export function TooltipTrigger(props: ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger {...props} />;
}

export function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          'z-50 rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background shadow-2',
          'animate-in fade-in-0 zoom-in-95 motion-reduce:animate-none',
          className,
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

/**
 * The whole tooltip for the common case: wrap a button, give it a label.
 *
 * `asChild` is what keeps the trigger from adding a wrapper element — the
 * button stays the direct child of the segmented control, so its layout and
 * keyboard order are unchanged by having a tooltip.
 */
export function Tooltip({
  label,
  children,
  side = 'bottom',
}: {
  label: string;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{label}</TooltipContent>
    </TooltipRoot>
  );
}
